/**
 * First-person controls: mouse look, WASD, gravity, flying, and zoom.
 *
 * The arithmetic lives in `movement.ts` so it can be tested without a browser.
 * This file is the browser half: events, pointer lock, and collision.
 */

import { Vector3, type Camera } from 'three';
import { EYE_HEIGHT, PLAYER_HEIGHT, PLAYER_RADIUS, blockedByBulks, type Bulk } from '@lofi/shared';
import {
  LOOK_SCALE_STEP,
  OVERSHOOT_TO_RELEASE,
  cameraOffset,
  clampLookScale,
  clampPitch,
  clampZoom,
  computeVelocity,
  flightBoost,
  lookDelta,
  lookDirection,
  reachOvershoot,
  bodyFitsAt,
  fitsAtHeight,
  stepLagAfterRise,
  stepLagDecayed,
  type MoveInput,
} from './movement.ts';
import { isTypingTarget } from './keys.ts';
const GRAVITY = 24; // m/s²
const JUMP_SPEED = 8;
/** How far you can step up without jumping — a kerb, not a wall. */
const STEP_HEIGHT = 1.05;
/** 🧍 Your box, for bumping into sheep — the size `crowd.ts` gives the player. */
const PLAYER_BODY = { half: PLAYER_RADIUS, height: PLAYER_HEIGHT };

/* 🪜 The step-smoothing numbers and rules live in `movement.ts`, with the
   rest of the pure movement maths — this class needs a DOM and cannot be
   tested, and haugur's stairs deserved a rule that could be. */

/** Where the look sensitivity is remembered between visits. */
const LOOK_SCALE_KEY = 'lofi.lookScale';

/**
 * How far in front of an obstruction the zoomed-out camera stops, in metres.
 * Sitting exactly on the surface puts the near clip plane inside it.
 */
const CAMERA_SKIN = 1.4;

/**
 * 🔴 **How much of a cell is solid, 0…1 — not whether it is.**
 *
 * **T160/T109, 2026-08-27.** This returned a boolean until the hearth became a
 * quarter-height block, and `groundBelow` added a whole metre for anything
 * that came back true. `build-palette.py` had a warning on it saying exactly
 * that: *"the first solid half-height block leaves every player standing half a
 * metre in the air — and the symptom reads as 'physics is broken' with nothing
 * pointing at the shape that caused it."*
 *
 * ✅ **0 means you fall straight through**, which is what a non-solid block like
 * `kjarr` returns, so walking through scrub is unchanged.
 *
 * ⚠️ **The fix was one line, and far smaller than the warning feared, because
 * there is NO HORIZONTAL COLLISION in this game at all.** Movement resolves the
 * ground and nothing else, and `STEP_HEIGHT` is 1.05 m, so you step onto
 * anything one block or shorter. **A short block therefore needs a surface
 * height and no wall handling whatsoever.**
 */
export interface Solid {
  (x: number, y: number, z: number): number;
}

export interface PlayerState {
  readonly position: Vector3;
  readonly onGround: boolean;
  readonly flying: boolean;
  readonly yaw: number;
  readonly pitch: number;
  /** Zoom the player asked for, in metres. 0 is first person. */
  readonly zoom: number;
  /** Zoom actually used, after the camera was stopped by any ground in the way. */
  readonly effectiveZoom: number;
  readonly lookScale: number;
  /** Current flight speed multiplier — 1 until a direction has been held. */
  readonly flightBoost: number;
}

export class Controls {
  readonly position: Vector3;
  private readonly camera: Camera;
  private readonly isSolid: Solid;
  private readonly velocity = new Vector3();
  private readonly keys = new Set<string>();
  private yaw = 0;
  private pitch = 0;
  /**
   * Mouse movement pushed upward *after* the view stopped going up.
   *
   * See {@link Controls.checkReachForBar}. Reset by any downward movement, so it
   * measures one shove rather than a session's worth of looking around.
   */
  private upOvershoot = 0;
  private zoom = 0;

  /**
   * 🎡 **Called when the wheel should step along the on-hand row**, +1 or −1.
   *
   * 🔴 **A callback rather than a call, because this module may not know about
   * the connection.** Controls owns the input; what a slot change MEANS is the
   * game's business, and the server is what actually decides which slot is held
   * — exactly as the number keys and Tab already work.
   */
  onSlotStep?: (step: number) => void;
  private onGround = false;
  private flying = true;
  private locked = false;
  /** Whether something on screen — the open pack — has the keyboard. */
  private paused = false;
  /** 🛏️ D133 — lying in a bed: no walking, and the eye down at the pillow. */
  private lying = false;
  private lookScale = readLookScale();
  /** How long a flight direction has been held, for the speed boost. */
  private heldFor = 0;
  /**
   * 🪜 **How far BELOW the true eye the camera still is**, in metres, while it
   * catches up with a step. 0 for all but about a tenth of a second at a time.
   */
  private stepLag = 0;

  /**
   * 🐑 **The bodies that stand in your way** — every sheep, as the browser is
   * drawing it. T274.
   *
   * 🔴 **A callback, not a list**, because the flock changes every frame and
   * this module may not know about it — the same reason {@link onSlotStep} is
   * one.
   */
  bodies?: () => readonly Bulk[];

  constructor(camera: Camera, isSolid: Solid, start: Vector3) {
    this.camera = camera;
    this.isSolid = isSolid;
    this.position = start.clone();
    this.attach();
  }

  private attach(): void {
    const canvas = document.querySelector('canvas');

    document.addEventListener('keydown', (e) => {
      /* ⚠️ Not while typing. Without this, entering "Urðarstígur" in the Dev
         Tools search box (T73) walks the player, toggles flying on the F and
         resets the zoom on the R — all invisibly, because the window is over
         the world. See `keys.ts`. */
      if (isTypingTarget(e.target)) return;

      this.keys.add(e.code);

      /* Space scrolls the page and F11 fullscreens; a game that does either
         when you jump is unusable. */
      if (e.code === 'Space') e.preventDefault();

      /* ⚠️ `e.repeat` guard. keydown AUTO-REPEATS while a key is held, so
         without this, holding F for even a moment toggles flying dozens of
         times a second and you land on whichever parity it stopped at — which
         reads exactly like "flying is broken". */
      if (e.repeat) return;

      if (e.code === 'KeyF') this.flying = !this.flying;
      if (e.code === 'KeyR') this.zoom = 0;

      /* Mouse speed is a matter of taste and of the mouse, so it is the
         player's to set rather than a constant somebody has to guess right.
         ⚠️ Matched on `e.key` as well as `e.code`: `code` names the physical US
         key position, and on an Icelandic keyboard the keys printed [ and ] are
         not in the positions called BracketLeft and BracketRight. Comma and
         full stop are the layout-independent fallback. */
      if (e.key === '[' || e.code === 'BracketLeft' || e.code === 'Comma') {
        this.setLookScale(this.lookScale - LOOK_SCALE_STEP);
      }
      if (e.key === ']' || e.code === 'BracketRight' || e.code === 'Period') {
        this.setLookScale(this.lookScale + LOOK_SCALE_STEP);
      }
    });

    document.addEventListener('keyup', (e) => this.keys.delete(e.code));

    /* Pointer lock: the only way to get mouse-look without the cursor leaving
       the window. Requires a user gesture, so it hangs off a click. */
    canvas?.addEventListener('click', () => void canvas.requestPointerLock());
    document.addEventListener('pointerlockchange', () => {
      this.locked = document.pointerLockElement === canvas;
      /* Keys held when focus is lost would otherwise stay held forever. */
      if (!this.locked) this.keys.clear();
    });

    document.addEventListener('mousemove', (e) => {
      if (!this.locked) return;
      this.yaw -= lookDelta(e.movementX, this.lookScale);
      this.pitch = clampPitch(this.pitch - lookDelta(e.movementY, this.lookScale));
      this.checkReachForBar(e.movementY);
    });

    /**
     * 🎡 **The wheel chooses which slot is in your hand. Ctrl+wheel pulls the
     * camera back.**
     *
     * 🎯 **haugur, 2026-09-02:** *"scrolling with mouse should move between
     * inventory slots on on hand menu, ctl scroll should do the Zoom."*
     *
     * 🔴 **The two were the other way round until now**, and the swap is the
     * right one: the wheel is the hand-bar control every game of this kind uses,
     * and pulling the camera out is the rarer thing.
     *
     * ⚠️ **`passive: false` and `preventDefault`, for TWO different reasons.**
     * Without it the page scrolls under the game — and **Ctrl+wheel is the
     * browser's own page-zoom**, so the zoom branch in particular has to swallow
     * the event or the whole page grows instead of the view.
     *
     * ✅ **When the pointer is not locked the event is LET THROUGH**, so a panel
     * can scroll normally. ⚠️ That is a deliberate change: it used to zoom the
     * world from inside a menu, which nobody asked for and nobody could see.
     */
    document.addEventListener(
      'wheel',
      (e) => {
        if (e.ctrlKey) {
          e.preventDefault();
          /* Proportional steps: fine control up close, coarse when far out. */
          const factor = 1 + Math.sign(e.deltaY) * 0.18;
          this.zoom = clampZoom(this.zoom === 0 ? (e.deltaY > 0 ? 4 : 0) : this.zoom * factor);
          return;
        }
        if (!this.locked) return;
        e.preventDefault();
        /* Down the wheel is along the row, which is what every other game does. */
        this.onSlotStep?.(Math.sign(e.deltaY));
      },
      { passive: false },
    );
  }

  get state(): PlayerState {
    return {
      position: this.position,
      onGround: this.onGround,
      flying: this.flying,
      yaw: this.yaw,
      pitch: this.pitch,
      zoom: this.zoom,
      effectiveZoom: this.clearZoom(),
      lookScale: this.lookScale,
      flightBoost: this.flying ? flightBoost(this.heldFor) : 1,
    };
  }

  private setLookScale(scale: number): void {
    this.lookScale = clampLookScale(scale);
    try {
      window.localStorage.setItem(LOOK_SCALE_KEY, String(this.lookScale));
    } catch {
      /* Private browsing, or storage disabled. Losing the setting between
         visits is a nuisance; failing to turn your head is not acceptable. */
    }
  }

  /**
   * Push the mouse up past the sky, and the game hands the cursor back, so the
   * status bar can be used (2026-08-11, haugur's choice).
   *
   * The decision itself is `reachOvershoot` in `movement.ts`, where it is pure
   * and tested — **there is no DOM test environment**, so anything left in here
   * is invisible to `pnpm gates`. This is only the part that must touch the
   * document.
   *
   * ⚠️ **It can still fire when somebody genuinely wants to stare straight up**
   * for four hundred pixels. If that turns out to be annoying, the honest fix is
   * the option not taken: delete this and let Esc do it.
   */
  private checkReachForBar(movementY: number): void {
    this.upOvershoot = reachOvershoot(this.upOvershoot, movementY, this.pitch);
    if (this.upOvershoot < OVERSHOOT_TO_RELEASE) return;
    this.upOvershoot = 0;
    document.exitPointerLock();
  }

  /**
   * ⚠️ How far the camera may actually pull back before it enters the ground.
   *
   * Without this, looking up while zoomed out drives the camera straight down
   * through the terrain — the pull-back is opposite the view direction, so
   * looking up means going down. From under the surface you see the world
   * through back faces, which are culled, so the ground vanishes and what is
   * left looks inside-out. It reads exactly like the world flipping over.
   *
   * ⚠️ Chunks that have not loaded read as air, so the camera can still pass
   * through terrain that has not arrived yet. That is a streaming problem
   * (T42), not one this can solve.
   */
  private clearZoom(): number {
    if (this.zoom <= 0) return 0;

    const direction = cameraOffset(this.yaw, this.pitch, 1);
    /* 🪜 The eye as DRAWN, lag and all — this ray decides where a wall stops
       the zoomed-out camera, and it has to be cast from where that camera
       actually is. */
    const eyeY = this.eyeY();
    /* Bounded sample count: at MAX_ZOOM a 1 m step would be 400 lookups every
       frame, and a metre of precision is invisible 400 m out. */
    const step = Math.max(1, this.zoom / 64);

    for (let t = step; t <= this.zoom; t += step) {
      const solid = this.isSolid(
        Math.floor(this.position.x + direction.x * t),
        Math.floor(eyeY + direction.y * t),
        Math.floor(this.position.z + direction.z * t),
      );
      /* ⚠️ Any solid part at all pushes the camera in, even a quarter-height
         one. Conservative on purpose: pulling the camera closer than it needs
         to be is a shrug, and letting it slide inside the ground is not. */
      if (solid > 0) return Math.max(0, t - CAMERA_SKIN);
    }

    return this.zoom;
  }

  get pointerLocked(): boolean {
    return this.locked;
  }

  /**
   * 🎒 **Stop driving the player, without forgetting where they are** (T152).
   *
   * The open pack is over the world and the mouse is free, so W and A are
   * letters again rather than directions. ⚠️ **Held keys are cleared as well as
   * ignored** — a key held down when the pack opened would otherwise still be
   * held when it closed, and the player would set off walking for a reason
   * nothing on screen explains. That is the same fault `pointerlockchange`
   * already clears for.
   *
   * 🔴 **Not the same thing as losing the pointer lock.** Releasing the mouse to
   * use the status bar leaves you standing in a world that still moves; this is
   * the game waiting for you.
   */
  /**
   * 🛏️ **Lie down, or get up** — D133. Lying, the keys move nothing (a key
   * wakes you instead, which `main.ts` decides) and the eye drops to the
   * pillow, looking along the bed and up at the roof, the way a person lying on
   * their back sees a room. The mouse still looks about.
   *
   * @param yaw which way the bed runs, head to foot — the view faces the foot.
   */
  setLying(lying: boolean, yaw?: number): void {
    this.lying = lying;
    this.keys.clear();
    if (lying) {
      if (yaw !== undefined) this.yaw = yaw;
      this.pitch = clampPitch(0.9);
    }
  }

  get isLying(): boolean {
    return this.lying;
  }

  setPaused(paused: boolean): void {
    if (paused === this.paused) return;
    this.paused = paused;
    this.keys.clear();
  }

  private get input(): MoveInput {
    /* ⚠️ Read as "nothing is held" rather than skipping `update` entirely —
       gravity, the ground check and the camera all still have to run, or opening
       the pack in mid-air would freeze the player where they stand. */
    if (this.paused || this.lying) {
      return {
        forward: false,
        back: false,
        left: false,
        right: false,
        up: false,
        down: false,
        run: false,
      };
    }
    return {
      forward: this.keys.has('KeyW'),
      back: this.keys.has('KeyS'),
      left: this.keys.has('KeyA'),
      right: this.keys.has('KeyD'),
      /* Space up, Shift down — the Minecraft convention, and both are safe.
         Ctrl is deliberately NOT used for descending: Ctrl+W closes the tab. */
      up: this.keys.has('Space'),
      down: this.keys.has('ShiftLeft') || this.keys.has('ShiftRight'),
      /* Shift means "descend" while flying, so running is Alt in the air. */
      run: this.flying
        ? this.keys.has('AltLeft') || this.keys.has('AltRight')
        : this.keys.has('ShiftLeft') || this.keys.has('ShiftRight'),
      /* 🤫 Z held — decision 134, haugur's pick. ⚠️ NOT Ctrl: Ctrl+W closes the
         tab and a page cannot stop it, the same reason Ctrl never descends. */
      sneak: !this.flying && this.keys.has('KeyZ'),
    };
  }

  /** Height of the ground column under a position, or null if it is all air. */
  private groundBelow(x: number, y: number, z: number): number | null {
    const bx = Math.floor(x);
    const bz = Math.floor(z);
    /* Scan down a bounded distance rather than to the bottom of the world:
       falling through unloaded chunks should not cost a 2,400-block loop. */
    for (let by = Math.floor(y); by > Math.floor(y) - 64; by--) {
      /* 🔴 The block's own height, not a whole metre. A quarter-height hearth
         puts your feet at by + 0.25; a full block still puts them at by + 1. */
      const height = this.isSolid(bx, by, bz);
      if (height > 0) return by + height;
    }
    return null;
  }

  update(dt: number): void {
    /* Clamp: a background tab returns with a multi-second dt, and moving 40 m
       in one step tunnels straight through the ground. */
    const step = Math.min(dt, 0.05);
    const input = this.input;
    const wish = computeVelocity(input, this.yaw, this.flying);

    /* The boost builds only while a direction is actually held, and resets the
       moment it is let go — otherwise pausing to look at something and setting
       off again would launch you. */
    const moving =
      input.forward || input.back || input.left || input.right || input.up || input.down;
    this.heldFor = this.flying && moving ? this.heldFor + step : 0;

    if (this.flying) {
      const boost = flightBoost(this.heldFor);
      this.position.x += wish.x * boost * step;
      this.position.y += wish.y * boost * step;
      this.position.z += wish.z * boost * step;
      this.velocity.set(0, 0, 0);
      this.onGround = false;
    } else {
      this.velocity.x = wish.x;
      this.velocity.z = wish.z;
      this.velocity.y -= GRAVITY * step;

      if (this.onGround && input.up) this.velocity.y = JUMP_SPEED;

      const next = this.position.clone().addScaledVector(this.velocity, step);

      /**
       * 🧱 **Walls stop you — haugur, 2026-09-01.**
       *
       * *"if i walk towards a tree and right up to it, instead of beeing blocked
       * i just go to the top of the tree… i can basically walk through things,
       * just transported to the top of world."*
       *
       * 🔴 **There was no horizontal collision at all**, and the comment on
       * {@link Solid} said so. The vertical resolve below then did the damage:
       * it scans DOWN from your feet plus a step, so a trunk you had walked
       * *inside* was found and you were lifted onto it, every frame.
       *
       * ⚠️ **Each axis is resolved on its own, and that is what lets you SLIDE.**
       * Rejecting the whole move when either axis is blocked pins you to a wall
       * you are walking along at an angle, which reads as the game catching on
       * something. Testing X and Z separately means the free one still happens.
       *
       * 🔴 **And if you are already stuck, nothing is blocked.** A player who
       * ends up inside a block — through a build, a teleport, or a chunk arriving
       * late — must be able to walk out. Refusing every direction would trap
       * them where nothing but a reload could help.
       */
      const stuck = !bodyFitsAt(
        this.isSolid,
        this.position.x,
        this.position.y,
        this.position.z,
        STEP_HEIGHT,
      );
      if (!stuck) {
        if (!bodyFitsAt(this.isSolid, next.x, this.position.y, this.position.z, STEP_HEIGHT)) {
          next.x = this.position.x;
          this.velocity.x = 0;
        }
        if (!bodyFitsAt(this.isSolid, next.x, this.position.y, next.z, STEP_HEIGHT)) {
          next.z = this.position.z;
          this.velocity.z = 0;
        }
      }

      /**
       * 🐑 **And a sheep stops you like a wall — T274.** 🎯 haugur,
       * 2026-09-24: *"a sheep must block you."*
       *
       * ⚠️ **The same x-then-z split as the walls above, for the same reason:
       * you slide along a sheep you walk into at an angle.**
       *
       * 🔴 **You are never trapped.** Already inside a sheep — it walked into
       * you — you may still move AWAY from it (`blockedByBulks`); and the
       * server moves any sheep you press out of your way, so a ring of them
       * gives. `crowd.ts` says why.
       */
      const bodies = this.bodies?.() ?? [];
      if (bodies.length > 0) {
        const here = { x: this.position.x, y: this.position.y, z: this.position.z };
        if (blockedByBulks(PLAYER_BODY, here, { ...here, x: next.x }, bodies)) {
          next.x = this.position.x;
          this.velocity.x = 0;
        }
        const across = { ...here, x: next.x };
        if (blockedByBulks(PLAYER_BODY, across, { ...across, z: next.z }, bodies)) {
          next.z = this.position.z;
          this.velocity.z = 0;
        }
      }

      /**
       * ⬆️ **RISING IS NOT LANDING — #561, and it is the tree bug underground.**
       *
       * 🎯 **Reported from inside the game, 2026-09-09**, by the first player
       * who was not haugur: *"Jumping underground spawns you overground."*
       *
       * 🔴 **`groundBelow` starts its scan a full `STEP_HEIGHT` ABOVE the
       * feet**, and takes the first solid thing it finds on the way down. On the
       * surface there is nothing above you, so it was harmless for months.
       * **Underground, the roof of your tunnel is above you** — so the roof was
       * found, called "the ground", and you were stood on top of it. ⚠️ **Then
       * again the next frame, and the next**: a ride up through the rock to the
       * surface, one block at a time, which the camera smoothing renders as a
       * teleport.
       *
       * ⚠️ **It is the SAME FAULT as walking into a tree**, described in the
       * comment above this one — *"the vertical resolve below then did the
       * damage"*. That was fixed for walking, by adding horizontal collision.
       * **The vertical path was left exactly as it was.**
       *
       * ✅ **A jump may not be resolved by snapping to ground.** While you are
       * moving upward there is no landing to find: gravity turns the velocity
       * over at the top of the arc, and the fall resolves normally from there.
       */
      const rising = this.velocity.y > 0;
      if (rising) {
        /**
         * 🧱 **And your head stops at a ceiling**, rather than passing into it.
         *
         * 🔴 **Nothing checked for solid ABOVE the player at all.** Jumping in
         * a tunnel put your head inside the roof, and the snap above was what
         * "rescued" you — onto it. With the snap gone, a jump without this would
         * simply bury your eye in rock for the length of the arc.
         *
         * ⚠️ **Only if you FIT where you are.** Somebody already inside a block
         * — through a build, a teleport, or a chunk arriving late — must be able
         * to move out. This is the same reasoning as `stuck` above, and refusing
         * the move for them would pin them where nothing but a reload helps.
         */
        if (
          !fitsAtHeight(this.isSolid, next.x, next.y, next.z) &&
          fitsAtHeight(this.isSolid, next.x, this.position.y, next.z)
        ) {
          next.y = this.position.y;
          this.velocity.y = 0;
        }
        this.onGround = false;
      } else {
        this.resolveDescent(next);
      }

      this.position.copy(next);
    }

    /* 🪜 The eye catches up at a fixed speed, so a tall step takes longer than
       a shallow one — which is what climbing looks like. */
    this.stepLag = stepLagDecayed(this.stepLag, step);

    this.applyCamera();
  }

  /**
   * 🪜 **Landing, stepping up, or staying in the air** — everything that is NOT
   * a rise.
   *
   * 🔴 **Split out of `update` for #561.** The rising case must not reach any of
   * this, and a branch this long left inline is how somebody adds a line to the
   * wrong side of it later.
   */
  private resolveDescent(next: Vector3): void {
    const ground = this.groundBelow(next.x, next.y + STEP_HEIGHT, next.z);

    if (ground === null || next.y > ground) {
      this.onGround = false;
      return;
    }

    /**
     * 🪜 **A STEP UP, not a landing — and the difference is the whole fix.**
     *
     * 🔴 **Only a rise while ALREADY STANDING is a step.** Coming down onto the
     * ground after a fall reaches this same line, and smoothing that would make
     * every landing float — the fall was already spread over many frames by
     * gravity and needs no help.
     *
     * ⚠️ **Clamped to `STEP_HEIGHT`.** A player put somewhere by a spawn, a
     * teleport or a chunk arriving late can rise any distance at all, and an
     * unclamped lag would leave the eye tens of metres underground looking up
     * through the world.
     */
    this.stepLag = stepLagAfterRise(
      this.stepLag,
      ground - this.position.y,
      this.onGround,
      STEP_HEIGHT,
    );
    next.y = ground;
    this.velocity.y = 0;
    this.onGround = true;
  }

  private applyCamera(): void {
    const offset = cameraOffset(this.yaw, this.pitch, this.clearZoom());
    this.camera.position.set(
      this.position.x + offset.x,
      this.eyeY() + offset.y,
      this.position.z + offset.z,
    );
    this.camera.rotation.set(this.pitch, this.yaw, 0, 'YXZ');
  }

  /**
   * 🪜 **How high the eye is right now** — the feet plus {@link EYE_HEIGHT},
   * **less whatever of the last step it has not caught up with yet.**
   *
   * 🎯 **haugur, 2026-09-09:** walking up steps looked *"like my character is
   * not moving up but the whole world is movin down."* 🔴 **The body still
   * moves in one frame** — physics, collision and everything the server is told
   * are untouched. **Only the eye lags**, for about a tenth of a second.
   *
   * 🔴 **AIMING USES THIS TOO, AND THAT IS DELIBERATE.** This project has
   * already written down what happens when the camera and the aim ray disagree:
   * *the camera is pulled in by a wall while the aim ray is not, which is why
   * Java deletes the crosshair in third person.* **You must aim from where you
   * can see.** ⚠️ The server judges reach from your feet, and this can differ
   * from them by at most {@link STEP_HEIGHT} for a tenth of a second — far
   * inside the reach it allows.
   */
  private eyeY(): number {
    /* 🛏️ Lying, the eye is a hand above the mattress, not a standing height. */
    if (this.lying) return this.position.y + 0.25;
    return this.position.y + EYE_HEIGHT - this.stepLag;
  }

  /**
   * Where the player's eye is, in world coordinates (T52).
   *
   * ⚠️ **The eye, not the camera.** Zooming out moves the camera hundreds of
   * metres backwards, and aiming from there would let you break blocks from the
   * far side of the town — the server would refuse them, and the refusal would
   * be blamed on the reach limit rather than on aiming from the wrong point.
   */
  eyeWorldPosition(): Vector3 {
    return new Vector3(this.position.x, this.eyeY(), this.position.z);
  }

  /** Which way the player is looking — a unit vector in world space (T52). */
  lookDirection(): Vector3 {
    const d = lookDirection(this.yaw, this.pitch);
    return new Vector3(d.x, d.y, d.z);
  }

  /**
   * Where the camera is, in world coordinates — for the renderer's offset.
   *
   * 🔴 **THIS IS THE ONE THAT DECIDES WHAT YOU SEE, NOT {@link applyCamera}.**
   * `main.ts` re-places the camera from this every single frame, rebased into
   * render-origin space — so **anything applied only in `applyCamera` is
   * silently thrown away.**
   *
   * ⚠️ **The step smoothing was written into `applyCamera` first and did
   * NOTHING**, and it would have shipped green: no test covers where the camera
   * ends up, the code read correctly, and the only symptom would have been
   * haugur saying the stairs were still funky. **A second way in forgets what
   * the first one does — four times before this, and this is the fifth.**
   * 👉 **Both use {@link eyeY} now, so they cannot disagree again.**
   */
  cameraWorldPosition(): Vector3 {
    const offset = cameraOffset(this.yaw, this.pitch, this.clearZoom());
    return new Vector3(
      this.position.x + offset.x,
      this.eyeY() + offset.y,
      this.position.z + offset.z,
    );
  }

  /**
   * Lift the player into the air, to be dropped again by {@link settle}.
   *
   * Used when the world underneath is replaced — switching the flat view on or
   * off (T49) moves the ground by tens of metres, and the alternative is
   * standing inside a hill or hanging over a hole. Re-using the spawn path
   * means the player lands on whatever arrives, without this code having to
   * know how high it will be.
   */
  refloat(altitude: number): void {
    this.position.y = altitude;
    this.flying = true;
    this.velocity.set(0, 0, 0);
  }

  /**
   * Put the player somewhere else entirely (T73).
   *
   * ⚠️ **The look direction is deliberately kept.** Teleporting is a way of
   * inspecting the world, and being spun round on arrival means reorienting
   * every single time.
   *
   * Arrives flying with the velocity cleared, the same as {@link refloat},
   * because the ground at the far end has not streamed in yet and any downward
   * velocity would be a fall through an empty world. The caller decides
   * whether to land — see `settled` in `main.ts`.
   */
  teleport(x: number, z: number, altitude: number): void {
    this.position.set(x, altitude, z);
    this.flying = true;
    this.velocity.set(0, 0, 0);
  }

  /** Drop to the ground, once terrain under the player has loaded. */
  settle(): boolean {
    if (!this.flying) return true;
    const ground = this.groundBelow(this.position.x, this.position.y, this.position.z);
    if (ground === null) return false;
    this.position.y = ground;
    this.flying = false;
    this.velocity.set(0, 0, 0);
    return true;
  }
}

/** The player's remembered look sensitivity, or the default. */
function readLookScale(): number {
  try {
    const stored = window.localStorage.getItem(LOOK_SCALE_KEY);
    return stored === null ? 1 : clampLookScale(Number(stored));
  } catch {
    return 1;
  }
}
